import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth, cachedGet } from '@onvice/portal-shell-api';
import { startFederationStatusStream, stopFederationStatusStream } from './lib/federation-status-stream';
import { initDynamicRemotes } from './modules/registry';
import { ActiveContextProvider } from './context/ActiveContext';
import { ThemeProvider } from './context/ThemeContext';
import { AssistantStateProvider } from './context/AssistantStateContext';
import { ModuleRegistryProvider } from '@onvice/portal-ui';
import ProtectedRoute from './components/ProtectedRoute';
// PortalShell lazy (2026-08-02 Federation-Resilience): PortalShell haelt statische
// Federation-Imports (TaskSuggestionsBell, MobileHomeTabBar, GpsSuggestModal aus
// bauberichteV2/public). Wird PortalShell eager geladen, killt jeder bauberichteV2-
// Container-Ausfall den Portal-Boot bereits vor dem Login-Screen. Lazy-Load
// verschiebt den Federation-Fetch auf den ersten geschuetzten Route-Wechsel —
// /login bleibt bootfaehig, Fehler bleibt lokal.
const PortalShell = lazy(() => import('./components/PortalShell'));
import ModuleRouter from './components/ModuleRouter';
import StageBanner from './components/StageBanner';
import IosInstallHint from './components/IosInstallHint';
import LoginPage from './pages/LoginPage';
import ImpressumPage from './pages/ImpressumPage';
import AdminMandantPage from './pages/admin/AdminMandantPage';
import BlankPage from './pages/BlankPage';
import PublicCreateTicketPage from './pages/PublicCreateTicketPage';
import PublicTicketPage from './pages/PublicTicketPage';
import HomePage from './pages/HomePage';
import DashboardHome from './pages/DashboardHome';
import MobileHomeIndex from './pages/MobileHomeIndex';
import AssistantPage from './pages/AssistantPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import GenehmigungenPage from './pages/GenehmigungenPage';
import HomeGF from './pages/HomeGF';
import UebersichtHeute from './pages/uebersicht/UebersichtHeute';
// UebersichtFuehrung lazy (2026-08-02 Federation-Resilience): enthaelt statischen
// Runtime-Import von zeiterfassung/public — wuerde bei Boot-Crash sonst Login killen.
const UebersichtFuehrung = lazy(() => import('./pages/uebersicht/UebersichtFuehrung'));
import {
  UebersichtAufgaben,
  UebersichtBerichte,
  UebersichtPlantafel,
  UebersichtZeiterfassung,
  UebersichtMaterial,
  UebersichtBibliothek,
  UebersichtAuswertungen,
  UebersichtVerwaltung,
} from './pages/uebersicht/UebersichtTab';
import UebersichtAdressbuch from './pages/uebersicht/UebersichtAdressbuch';
import UebersichtKunden from './pages/uebersicht/UebersichtKunden';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserFormPage from './pages/admin/AdminUserFormPage';
// AdminDepartmentsPage 2026-05-19 (R3) durch /admin/adressbuch abgelöst — Datei bleibt im Repo
// import AdminDepartmentsPage from './pages/admin/AdminDepartmentsPage';
import AdminRolesPage, { AdminRoleFormPage } from './pages/admin/AdminRolesPage';
import AdminAdressbuchPage from './pages/admin/AdminAdressbuchPage';
import AdminDmsTypesPage from './pages/admin/AdminDmsTypesPage';
import AdminUserPagesPage from './pages/admin/AdminUserPagesPage';
import AdminPageTemplatesPage from './pages/admin/AdminPageTemplatesPage';
import AdminSidebarLabelsPage from './pages/admin/AdminSidebarLabelsPage';
import AdressbuchPersonAkte from './pages/admin/AdressbuchPersonAkte';
import AdressbuchPersonNeu from './pages/admin/AdressbuchPersonNeu';
// Seit Federation-Self-Healing (2026-08-02): keine statischen Cross-Modul-
// Imports mehr in diesem Component-Baum. AdressbuchContactAkte darf wieder
// eager, weil useFederatedModule statt statischem Import verwendet wird.
import AdressbuchContactAkte from './pages/admin/AdressbuchContactAkte';
import AdressbuchContactNeu from './pages/admin/AdressbuchContactNeu';
import AdminAdressbuchGroupDetailPage from './pages/admin/AdminAdressbuchGroupDetailPage';
import AdminAdressbuchModuleDetailPage from './pages/admin/AdminAdressbuchModuleDetailPage';
import SettingsPage from './pages/SettingsPage';
import TodosPage from './pages/TodosPage';
import KalenderPage from './pages/KalenderPage';
import SearchPage from './pages/SearchPage';
import TestCockpitPage from './modules/test-cockpit/pages/TestCockpitPage';
import { useStageDocumentTitle } from './lib/useStage';

// Module registrieren
import './modules/registry';

// Legacy: alter Pfad /m/stammdaten/mitarbeiter/:userId leitet auf die
// Adressbuch-Akte um (per by-user-Resolver). Mitarbeiterakte-Welle 2026-06-14:
// Mitarbeiter werden ausschliesslich im Adressbuch gepflegt.
function LegacyMitarbeiterRedirect() {
  const { userId } = useParams<{ userId: string }>();
  if (!userId) return <Navigate to="/uebersicht/adressbuch" replace />;
  return <Navigate to={`/admin/adressbuch/persons/by-user/${userId}`} replace />;
}

// 2026-07-06 (P4.1): Aktenstruktur zog vom /admin-Bereich ins Vorlagen-Modul.
// Alte Bookmarks + Deep-Links (?typ=…) landen weiterhin am Ziel.
function AktenstrukturRedirect() {
  const [sp] = useSearchParams();
  const qs = sp.toString();
  return <Navigate to={`/m/vorlagen/aktenstruktur${qs ? `?${qs}` : ''}`} replace />;
}

// createBrowserRouter (statt BrowserRouter) ist Pflicht damit useBlocker funktioniert
// (RR v7 Library-Mode mit BrowserRouter unterstuetzt keine data-router-Hooks).
const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Fund 10 (2026-09-28): öffentliches Impressum, ohne Login (Login-Seite verlinkt es).
  { path: '/impressum', element: <ImpressumPage /> },
  // Service-Desk Public-Pages — kein Login, Token-Auth (Auth-Layer im Backend-Public-Proxy)
  { path: '/ticket/erstellen/:token', element: <PublicCreateTicketPage /> },
  { path: '/ticket/:token', element: <PublicTicketPage /> },
  { path: '/x7q3v9k2/:_t', element: <BlankPage /> },
  { path: '/m4r8t1z6/:_t', element: <BlankPage /> },
  {
    element: <ProtectedRoute><Suspense fallback={null}><PortalShell /></Suspense></ProtectedRoute>,
    children: [
      { path: '/', element: <HomePage /> },
      // 2026-06-02 (Joerg): /apps zeigt den klassischen Modul-Picker auf Desktop,
      // ohne den /-Redirect auf /uebersicht/heute. Dieselbe Komponente, nur ohne
      // den Auto-Redirect — der Toggle in der TopBar fuehrt hierher.
      { path: '/apps', element: <HomePage /> },
      { path: '/dashboard', element: <DashboardHome /> },
      // 2026-07-07 (Joerg): Genehmigungs-Sammelmappe fuer die GF — aggregiert
      // Abwesenheits-Antraege, Zeitkorrekturen, Aufgaben ohne Owner aus mehreren
      // Modulen. Sichtbarkeits-Guard prueft Backend (403 → Redirect).
      { path: '/genehmigungen', element: <GenehmigungenPage /> },
      // 2026-07-07 (Joerg): rollenspezifische GF-Startseite. Sidebar-„Startseite"
      // linkt fuer GF/Admin direkt hierher. Backend-403 → Redirect auf /.
      { path: '/home/gf', element: <HomeGF /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/todos', element: <TodosPage /> },
      { path: '/kalender', element: <KalenderPage /> },
      { path: '/suche', element: <SearchPage /> },
      { path: '/test-cockpit', element: <TestCockpitPage /> },
      { path: '/test-cockpit/:runId', element: <TestCockpitPage /> },
      { path: '/m/:moduleSlug/*', element: <ModuleRouter /> },
      { path: '/kunden/:id', element: <CustomerDetailPage /> },
      { path: '/uebersicht', element: <Navigate to="/uebersicht/heute" replace /> },
      { path: '/uebersicht/fuehrung', element: <UebersichtFuehrung /> },
      { path: '/uebersicht/page/:id', element: <UebersichtFuehrung /> },
      // Joerg 2026-06-05: Admin bearbeitet Seite im Auftrag eines anderen Users.
      // UebersichtFuehrung erkennt actAsUserId aus den URL-Params + leitet
      // WidgetGrid actAsUserId weiter → Save geht gegen Admin-Endpoints.
      { path: '/uebersicht/admin/:actAsUserId/page/:id', element: <ProtectedRoute adminOnly><UebersichtFuehrung /></ProtectedRoute> },
      { path: '/uebersicht/heute', element: <UebersichtHeute /> },
      { path: '/uebersicht/aufgaben', element: <UebersichtAufgaben /> },
      { path: '/uebersicht/berichte', element: <UebersichtBerichte /> },
      // 2026-05-29 (Joerg): /uebersicht/mitarbeiter ersetzt durch /uebersicht/adressbuch.
      // 2026-05-30 (Joerg): /uebersicht/kunden wieder als eigenstaendige Page — direkter
      // Zugriff auf Kundenakten ohne Umweg ueber Adressbuch (UebersichtKunden.tsx).
      { path: '/uebersicht/kunden', element: <UebersichtKunden /> },
      { path: '/uebersicht/mitarbeiter', element: <Navigate to="/uebersicht/adressbuch" replace /> },
      { path: '/uebersicht/adressbuch', element: <UebersichtAdressbuch /> },
      { path: '/uebersicht/plantafel', element: <UebersichtPlantafel /> },
      { path: '/uebersicht/zeiterfassung', element: <UebersichtZeiterfassung /> },
      { path: '/uebersicht/material', element: <UebersichtMaterial /> },
      { path: '/uebersicht/bibliothek', element: <UebersichtBibliothek /> },
      { path: '/uebersicht/auswertungen', element: <UebersichtAuswertungen /> },
      { path: '/uebersicht/verwaltung', element: <UebersichtVerwaltung /> },

      // Mobile-Homepage (Joerg 2026-05-22): /home redirected zur ersten konfigurierten
      // Mobile-Tab — Tabs zeigen direkt auf Modul-Roots (z.B. /m/zeiterfassung).
      // Die Module rendern mit ihrer eigenen BottomNav; die Mobile-Home-TabBar
      // bleibt persistent oben sichtbar solange der User in einem der Tabs ist.
      { path: '/home', element: <MobileHomeIndex /> },
      { path: '/assistant', element: <AssistantPage /> },
      // /admin/users + /admin/roles + /admin/departments abgelöst durch /admin/adressbuch
      // (Konsolidierungs-Plan 2026-05-18, R3 abgeschlossen 2026-05-19). Routes als
      // Redirect erhalten, damit alte Bookmarks weiterhin im neuen Adressbuch landen.
      { path: '/admin/users', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/users/new', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/users/:id', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/departments', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/roles', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/roles/new', element: <Navigate to="/admin/adressbuch" replace /> },
      { path: '/admin/roles/:id', element: <Navigate to="/admin/adressbuch" replace /> },
      // 2026-07-06 (P4.1): Aktenstruktur zog ins Vorlagen-Modul (Tab). Alte
      // Route wird per Wrapper-Redirect umgeleitet, ?typ=... bleibt erhalten.
      { path: '/admin/aktenstruktur', element: <AktenstrukturRedirect /> },
      // 2026-07-20 (DMS-D1): Doc-Type-Katalog nur fuer dms.configure-Rolle.
      // Sichtbarkeit via requiresPermission in Sidebar/MobileHomeTabBar.
      { path: '/admin/dms-typen', element: <AdminDmsTypesPage /> },
      // 2026-07-07 (Joerg): Adressbuch fuer Buero ebenfalls oeffnen (nicht mehr
      // adminOnly). Backend filtert per field-access, Sidebar-Filter blendet den
      // Menuepunkt fuer Handwerker aus.
      { path: '/admin/adressbuch', element: <AdminAdressbuchPage /> },
      // Mitarbeiterakte-Welle 2026-06-14: Detail-View ist jetzt die voll
      // ausgebaute Adressbuch-Person-Akte (4 Tabs). Alte
      // AdminAdressbuchPersonDetailPage bleibt im Repo als Referenz.
      // Person = Mitarbeiter. Zwei Einstiegspfade:
      //   /admin/adressbuch/persons/:id           (aus Adressbuch-Listing)
      //   /admin/adressbuch/persons/by-user/:userId (aus Plantafel/Bauberichte)
      //
      // /persons/new MUSS vor /persons/:id stehen — sonst matcht React Router
      // die :id-Route mit id="new" und schickt den Detail-Endpoint ins Leere.
      { path: '/admin/adressbuch/persons/new',                element: <ProtectedRoute adminOnly><AdressbuchPersonNeu /></ProtectedRoute> },
      { path: '/admin/adressbuch/persons/:id',                element: <ProtectedRoute><AdressbuchPersonAkte /></ProtectedRoute> },
      { path: '/admin/adressbuch/persons/by-user/:userId',    element: <ProtectedRoute><AdressbuchPersonAkte /></ProtectedRoute> },
      // Legacy-Pfade aus dem alten stammdaten-Modul leiten auf das Adressbuch.
      // Plantafel + Bauberichte verlinken historisch auf /m/stammdaten/mitarbeiter/:userId.
      { path: '/m/stammdaten/mitarbeiter',           element: <Navigate to="/uebersicht/adressbuch" replace /> },
      { path: '/m/stammdaten/mitarbeiter/:userId',   element: <LegacyMitarbeiterRedirect /> },
      // W6 (2026-07-06): Kontakt-Detail als eigene Route (analog Mitarbeiterakte).
      // /contacts/new MUSS vor /contacts/:id stehen — sonst matcht React Router
      // die :id-Route mit id="new" (analog zu /persons/new).
      { path: '/admin/adressbuch/contacts/new', element: <ProtectedRoute><AdressbuchContactNeu /></ProtectedRoute> },
      { path: '/admin/adressbuch/contacts/:id', element: <ProtectedRoute><AdressbuchContactAkte /></ProtectedRoute> },
      { path: '/admin/adressbuch/groups/:id', element: <ProtectedRoute adminOnly><AdminAdressbuchGroupDetailPage /></ProtectedRoute> },
      { path: '/admin/adressbuch/modules/:slug', element: <ProtectedRoute adminOnly><AdminAdressbuchModuleDetailPage /></ProtectedRoute> },
      // Joerg 2026-06-05: Admin verwaltet persoenliche Seiten anderer User.
      { path: '/admin/user-pages', element: <ProtectedRoute adminOnly><AdminUserPagesPage /></ProtectedRoute> },
      { path: '/admin/user-pages/:userId', element: <ProtectedRoute adminOnly><AdminUserPagesPage /></ProtectedRoute> },
      // Joerg 2026-06-08: Admin verwaltet Seiten-Vorlagen (Default-Pages).
      { path: '/admin/page-templates', element: <ProtectedRoute adminOnly><AdminPageTemplatesPage /></ProtectedRoute> },
      // rf9 (2026-08-17): Sidebar-Labels editieren (Tenant-Override auf portal_app_modules.nav_label_alias)
      { path: '/admin/sidebar-labels', element: <ProtectedRoute adminOnly><AdminSidebarLabelsPage /></ProtectedRoute> },
      { path: '/admin/mandant', element: <ProtectedRoute adminOnly><AdminMandantPage /></ProtectedRoute> },
      { path: '/admin/page-templates/:templateId', element: <ProtectedRoute adminOnly><UebersichtFuehrung /></ProtectedRoute> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

/**
 * Startet den Federation-Health-SSE-Stream sobald der User eingeloggt ist —
 * und schliesst ihn beim Logout. Fix fuer den 401-Loop auf /login (SSE hat
 * kein Auto-Refresh, das apiFetch bietet). Muss innerhalb <AuthProvider> stehen.
 */
function AuthEffects() {
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) return;
    startFederationStatusStream();
    // rf9 (2026-08-17): dynamische Federation-Remotes laden.
    // Bestandsmodule kommen aus der statischen REMOTES-Map in registry.ts;
    // neue Module (Faktura + Nachfolger) sind hier per DB registriert und
    // werden zur Laufzeit ueber registerRemotes()+loadRemote() angehaengt.
    // Fail-soft: 401 vor Login-Refresh oder Netzfehler → keine dynamischen
    // Module in der Sidebar, statische Module laufen normal weiter.
    void (async () => {
      try {
        // apiFetch prependet automatisch `/api` — hier NUR /portal/... uebergeben.
        // Backend liefert { success, data: [...] } — apiFetch reicht das unwrapped
        // durch (kein Auto-Unwrap), deshalb .data extrahieren.
        // PWA-Offline (Alex-Fund 2026-09-09): cachedGet — offline kommt die
        // letzte Modul-Liste, remoteEntry.js liefert der SW aus dem Cache.
        const res = await cachedGet<{ success: boolean; data: Array<{ slug: string; remoteEntry: string }> }>(
          '/portal/modules/available'
        );
        const list = res?.data ?? [];
        const remotes = list
          .filter((m) => m && m.slug && m.remoteEntry)
          .map((m) => ({ slug: m.slug, url: m.remoteEntry }));
        if (remotes.length > 0) {
          await initDynamicRemotes(remotes);
        }
      } catch (err) {
        console.warn('[AuthEffects] dynamische Remotes nicht ladbar:', (err as Error)?.message ?? err);
      }
    })();
    return () => stopFederationStatusStream();
  }, [isAuthenticated]);
  return null;
}

export default function App() {
  useStageDocumentTitle('onvice Portal');
  return (
    <AuthProvider>
      {/* ThemeProvider INNERHALB AuthProvider (Martin-Fund 2026-09-17): liest den
          gespeicherten Hell/Dunkel-Modus bei Login/Userwechsel nach. */}
      <ThemeProvider>
        <AuthEffects />
        <ActiveContextProvider>
          <ModuleRegistryProvider>
            <AssistantStateProvider>
              <StageBanner />
              <IosInstallHint />
              <RouterProvider router={router} />
            </AssistantStateProvider>
          </ModuleRegistryProvider>
        </ActiveContextProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
